import { type ReactNode, useId } from 'react';

type BaseOptions = {
    id?: string;
    description?: string;
    error?: boolean;
};

type BaseWiring = {
    controlId: string;
    controlProps: {
        id: string;
        'aria-describedby': string | undefined;
        'aria-invalid': true | undefined;
    };
    dataInvalid: true | undefined;
    descriptionId: string | undefined;
};

export function useFieldBaseWiring({ id, description, error }: BaseOptions): BaseWiring {
    const reactId = useId();
    const controlId = id ?? reactId;
    const hasError = !!error || undefined;
    const descriptionId = description ? `${controlId}-description` : undefined;

    return {
        controlId,
        controlProps: {
            id: controlId,
            'aria-describedby': descriptionId,
            'aria-invalid': hasError,
        },
        dataInvalid: hasError,
        descriptionId,
    };
}

type Options = {
    id?: string;
    description?: string;
    error?: boolean | ReactNode;
};

type Wiring = BaseWiring & {
    errorId: string | undefined;
    errorMessage: ReactNode | undefined;
};

export function useFieldWiring({ id, description, error }: Options): Wiring {
    const base = useFieldBaseWiring({ id, description, error: !!error });
    const errorMessage = typeof error === 'boolean' ? undefined : error;
    const errorId = errorMessage ? `${base.controlId}-error` : undefined;

    return {
        ...base,
        controlProps: {
            ...base.controlProps,
            'aria-describedby': errorId ?? base.controlProps['aria-describedby'],
        },
        errorId,
        errorMessage,
    };
}
